import React from 'react';
import DataTable from '../../components/datatable/DataTable';
import { translate } from 'react-i18next';
import { Button, ButtonGroup } from 'react-bootstrap';

class SCControlPanelTable extends React.Component {
  static convertToDevConfig(dev) {
    return {
      origin: {
        x: dev.x,
        y: dev.y,
        z: dev.z,
      },
      size: {
        width: dev.width,
        height: dev.height === undefined ? dev.length : dev.height,
      },
      xAngle: dev.angle,
      azimuth: dev.azimuth,
    };
  }

  static getConfigForDevice(type, windowWidth, windowHeight) {
    const defaultDeviceConfig = {
      origin: {
        x: 0,
        y: 0,
        z: 0,
      },
      size: {
        width: windowWidth,
        height: windowHeight,
      },
      isShow: true,
      isHighLight: false,
    };

    switch (type) {
      case 'right':
        return {
          ...defaultDeviceConfig,
          origin: {
            x: 0,
            y: windowWidth,
            z: 0,
          },
        };
      case 'top':
        return {
          ...defaultDeviceConfig,
          origin: {
            x: 0,
            y: 0,
            z: windowHeight,
          },
          xAngle: 75,
        };
      case 'front':
        return {
          ...defaultDeviceConfig,
          origin: {
            x: windowHeight,
            y: 0,
            z: windowHeight,
          },
          xAngle: 10,
        };
      default:
        return defaultDeviceConfig;
    }
  }

  constructor(props) {
    super(props);

    const { t } = props;

    this.columns = [
      {
        Header: t('name'),
        accessor: 'name',
      },
      {
        Header: `X (${t('meter abbrv')})`,
        accessor: 'origin.x',
        Cell: ({ index, value }) => this.renderCell(index, 'x', value, {}),
      },
      {
        Header: `Y (${t('meter abbrv')})`,
        accessor: 'origin.y',
        Cell: ({ index, value }) => this.renderCell(index, 'y', value, {}),
      },
      {
        Header: `Z (${t('meter abbrv')})`,
        accessor: 'origin.z',
        Cell: ({ index, value }) => this.renderCell(index, 'z', value, {}),
      },
      {
        Header: `${t('width')} (${t('meter abbrv')})`,
        accessor: 'size.width',
        Cell: ({ index, value }) => this.renderCell(index, 'width', value, { min: 0.01 }),
      },
      {
        Header: `${t('height')} (${t('meter abbrv')})`,
        accessor: 'size.height',
        Cell: ({ index, value }) => this.renderCell(index, 'height', value, { min: 0.01 }),
      },
      {
        Header: `${t('angle')} (°)`,
        accessor: 'xAngle',
        Cell: ({ index, value }) => this.renderCell(index, 'angle', value, { min: 0, max: 90 }),
      },
      {
        Header: `${t('azimuth')} (°)`,
        accessor: 'azimuth',
        Cell: ({ index, value }) => this.renderAzimuthCell(index, value),
      },
    ];

    this.state = {
      list: [
        { name: 'window', index: 0 },
        ...(props.shadingDevices.left || []).map((item, index) => ({ name: 'leftDevices', index})),
        ...(props.shadingDevices.right || []).map((item, index) => ({ name: 'rightDevices', index})),
        ...(props.shadingDevices.top || []).map((item, index) => ({ name: 'topDevices', index})),
        ...(props.shadingDevices.front || []).map((item, index) => ({ name: 'frontDevices', index})),
      ],
      window: [{ name: 'Window', size: { width: props.window.width, height: props.window.height }}],
      leftDevices: props.shadingDevices.left || [],
      rightDevices: props.shadingDevices.right || [],
      topDevices: props.shadingDevices.top || [],
      frontDevices: props.shadingDevices.front || [],
    };

    this.creationCount = [
      ...this.state.leftDevices,
      ...this.state.rightDevices,
      ...this.state.topDevices,
      ...this.state.frontDevices,
    ]
      .map(dev => parseInt(dev.name.split(' ')[1], 10))
      .reduce((maxId, id) => Math.max(maxId, id), 0);
  }

  componentDidMount() {
    if (this.props.onReady) {
      this.props.onReady(this);
    }
  }

  renderAzimuthCell(index, initialValue) {
    const item = this.state.list[index];
    const device = this.state[item.name][item.index];
    const isEnabled = item.name === 'leftDevices' || item.name === 'rightDevices';
    return (
      <input
        style={{ width: '100%', textAlign: 'right' }}
        disabled={!isEnabled}
        type="number"
        min={-360}
        max={360}
        defaultValue={initialValue}
        onChange={({ target: { value } }) => {
          if (isEnabled) {
            this.changeDevice(item.name, device.name, this.convertParam(device, 'azimuth', value));
          }
        }}
        onFocus={() => isEnabled && this.focusDevice(device.name)}
        onBlur={() => isEnabled && this.focusDevice(null)}
      />
    );
  }

  renderCell(index, name, initialValue, props) {
    const item = this.state.list[index];
    const device = this.state[item.name][item.index];
    return (
      <input
        {...props}
        style={{ width: '100%', textAlign: 'right' }}
        disabled={initialValue === undefined}
        type="number"
        defaultValue={initialValue}
        onChange={({ target: { value } }) => this.changeDevice(item.name, device.name, this.convertParam(device, name, value))}
        onFocus={() => this.focusDevice(device.name)}
        onBlur={() => this.focusDevice(null)}
      />
    );
  }

  convertParam(currentParams, name, newValue) {
    let template;
    if (name === 'window') {
      template = {
        width: currentParams.size.width,
        height: currentParams.size.height,
      };
    } else {
      template = {
        x: currentParams.origin.x,
        y: currentParams.origin.y,
        z: currentParams.origin.z,
        width: currentParams.size.width,
        height: currentParams.size.height,
        angle: currentParams.xAngle,
        azimuth: currentParams.azimuth,
      };
    }

    return {
      ...template,
      [name]: parseFloat(newValue),
    };
  }

  addDevice(deviceType, stateKey) {
    this.creationCount++;
    const window = this.state.window[0];
    const addedDev = {
      ...SCControlPanelTable.getConfigForDevice(deviceType, window.size.width, window.size.height),
      name: `Device ${this.creationCount}`,
    };

    this.setState(
      {
        [stateKey]: [...this.state[stateKey], addedDev],
        list: [
          ...this.state.list,
          { name: stateKey, index: this.state[stateKey].length },
        ],
      },
      () => this.notifyDataChanged(),
    );
  }

  changeDevice(stateKey, name, data) {
    const newList = this.state[stateKey].map(
      item => item.name === name ? ({ ...item, ...SCControlPanelTable.convertToDevConfig(data) }) : item
    );
    this.setState(
      {
        [stateKey]: newList,
      },
      () => this.notifyDataChanged(),
    );
  }

  removeDevice(listIndex) {
    let item = this.state.list[listIndex];
    this.setState(
      {
        list: this.state.list.filter((item, index) => listIndex !== index),
        [item.name]: this.state[item.name].filter((_, index) => index !== item.index),
      },
      () => this.notifyDataChanged(),
    );
  }

  focusDevice(name) {
    const updatedDevices = Object.keys(this.state)
      .filter(key => key.toLowerCase().indexOf('devices') !== -1)
      .reduce((acc, deviceType) => {
        const newList = this.state[deviceType].map(device => ({ ...device, isHighLight: device.name === name}));
        return { ...acc, [deviceType]: newList };
      }, {});

    this.setState(updatedDevices, () => this.notifyDataChanged());
  }

  notifyDataChanged() {
    const { onDataChanged } = this.props;
    if (onDataChanged) {
      const renderData = Object.keys(this.state)
        .filter(key => key.toLowerCase().indexOf('devices') !== -1)
        .reduce(
          (devices, key) => {
            const filteredDevices = devices[key].filter(device => device.isShow);
            return { ...devices, [key]: filteredDevices };
          },
          { ...this.state },
        );
      const { list, window, ...data } = renderData;
      onDataChanged({
        ...data,
        windowWidth: this.state.window[0].size.width,
        windowHeight: this.state.window[0].size.height,
      });
    }
  }

  getCurrentData() {
    const renderData = Object.keys(this.state)
      .filter(key => key.toLowerCase().indexOf('devices') !== -1)
      .reduce(
        (devices, key) => {
          const filteredDevices = devices[key].filter(device => device.isShow);
          return { ...devices, [key]: filteredDevices };
        },
        { ...this.state },
      );
    const { list, window, ...data } = renderData;
    return {
      ...data,
      windowWidth: this.state.window[0].size.width,
      windowHeight: this.state.window[0].size.height,
      windowMode: this.props.windowMode || 'rectangle',
    };
  }

  render() {
    const { t } = this.props;
    return (
      <div style={{ textAlign: 'right' }}>
        <ButtonGroup>
          <Button onClick={() => this.addDevice('left', 'leftDevices')}>{t('add left device')}</Button>
          <Button onClick={() => this.addDevice('right', 'rightDevices')}>{t('add right device')}</Button>
          <Button onClick={() => this.addDevice('top', 'topDevices')}>{t('add top device')}</Button>
          <Button onClick={() => this.addDevice('front', 'frontDevices')}>{t('add front device')}</Button>
        </ButtonGroup>
        <DataTable
          columns={this.columns}
          sortable={false}
          data={this.state.list.map(item => this.state[item.name][item.index]).filter(item => !!item)}
          onActionDelete={({ original, index }) => this.removeDevice(index)}
          shouldShowActionMenu={(name, row) => {
            switch (name) {
              case DataTable.ACTION_CUSTOM:
                return row.original.name.toLowerCase() !== 'window';
              case DataTable.ACTION_DELETE:
                return row.original.name.toLowerCase() !== 'window';
              default:
                return true;
            }
          }}
        />
      </div>)
  }
}

export default translate('translations')(SCControlPanelTable);



// WEBPACK FOOTER //
// ./src/components/sc_editor/SCControlPanelTable.js